import {
  Box,
  Checkbox,
  FormHelperText,
  Link,
  Skeleton,
  Stack
} from "@mui/material";
import BaseTypography, {
  BaseTypographyProps
} from "@ui/atoms/BaseTypography/BaseTypography";
import { GridCard, GridCardSurface } from "@ui/atoms/GridCard/GridCard";
import { LAYOUT_GAP } from "@themes/tokens/shared";
import { ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

const AuthFormContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  background: `
    linear-gradient(
      to bottom,
      #FFFFFF 0%,
      rgba(var(--mui-palette-primary-mainChannel) / 0.14) 50%,
      rgba(var(--mui-palette-primary-mainChannel) / 0.34) 100%
    )
  `,
  display: "flex",
  height: "100vh",
  inset: 0,
  justifyContent: "center",
  minHeight: "100vh",
  ...(theme.applyStyles("dark", {
    background: `
    linear-gradient(
      to bottom,
      ${theme.vars.palette.primary.light} 0%,
      var(--mui-palette-primary-200) 50%,
      #000000 100%
    )
  `
  }) as CSSObject)
}));

const AuthFormCard = styled(Stack)(({ theme }) => ({
  backdropFilter: "blur(12px)",
  background: "rgba(255, 255, 255, 0.35)",
  border: "1px solid rgba(var(--mui-palette-primary-mainChannel) / 0.12)",
  borderRadius: theme.vars.shape.borderRadiusMd,
  boxShadow: " 0 8px 32px 0 rgba(0, 0, 0, 0.15)",
  display: "flex",

  flexDirection: "column",
  justifyContent: "center",
  maxWidth: "510px",
  minHeight: "300px",
  overflow: "hidden",
  padding: "36px",
  position: "relative",
  WebkitBackdropFilter: "blur(12px)",
  width: "100%",

  ...(theme.applyStyles("dark", {
    backdropFilter: "blur(10px)",
    background: "rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255,255,255,0.05)",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,.25)",
    WebkitBackdropFilter: "blur(10px)"
  }) as CSSObject)
}));

const AuthLogoLink = styled(Link)({
  margin: "0 auto !important"
});

const FormComponentsErrorBox = styled(FormHelperText)(({ theme }) => ({
  "&.Mui-error": {
    color: theme.vars.palette.error.main,
    marginLeft: 2
  }
}));

const StyledFormCheckbox = styled(Checkbox)(({ theme }) => {
  return {
    "& .MuiSvgIcon-root": {
      fontSize: theme.typography.h6.fontSize
    },
    color: theme.vars.palette.text.secondary,
    padding: "0"
  };
});

const FormSectionsContainer = styled(Stack)({
  gap: "24px",
  width: "100%"
});

/** Maps a legacy pixel font size to the closest BaseTypography variant. */
const fontSizeToVariant = (size: number): BaseTypographyProps["variant"] => {
  if (size <= 12) return "caption";
  if (size <= 15) return "body2";
  if (size === 16) return "subtitle1";
  if (size <= 19) return "h6";
  if (size === 20) return "h5";
  if (size <= 24) return "h4";
  if (size <= 32) return "h3";
  if (size <= 42) return "h2";
  return "h1";
};

export interface FormSectionTitleProps {
  /**
   * Rendered opposite the title, for an action that belongs to the SECTION
   * rather than to any field inside it — creating a row the section lists, say.
   * Optional, so a section that has no such action is laid out exactly as
   * before.
   */
  action?: ReactNode;
  title: string | number;
  description?: string;
  loading?: boolean;
  tooltipText?: string;
  titleSize?: number;
  titleColor?: string;
  descriptionSize?: number;
  descriptionColor?: string;
}

const FormSectionTitle = ({
  action,
  description,
  descriptionColor,
  descriptionSize = 14,
  loading = false,
  title,
  titleColor,
  titleSize = 16,
  tooltipText
}: FormSectionTitleProps) => {
  return (
    <Box
      sx={{
        alignItems: "flex-start",
        display: "flex",
        gap: 2,
        justifyContent: "space-between"
      }}
    >
      <Box>
        {loading ? (
          <>
            <Skeleton variant="text" width={150} height={32} />
            {description && <Skeleton variant="text" width={100} height={24} />}
          </>
        ) : (
          <>
            <BaseTypography
              loading={loading}
              variant={fontSizeToVariant(titleSize)}
              weight="semiBold"
              sx={{
                color: (theme) => titleColor ?? theme.vars.palette.text.primary
              }}
              text={`${title}`}
              tooltipText={tooltipText}
              tooltipPlacement="right"
            />

            {description && (
              <BaseTypography
                loading={loading}
                variant={fontSizeToVariant(descriptionSize)}
                weight="regular"
                text={`${description}`}
                sx={{
                  color: (theme) =>
                    descriptionColor ?? theme.vars.palette.text.secondary
                }}
              />
            )}
          </>
        )}
      </Box>
      {action && !loading ? <Box>{action}</Box> : null}
    </Box>
  );
};

export interface FormSectionProps {
  /** @see `FormSectionTitleProps.action`. Requires a `title` to render. */
  action?: ReactNode;
  title?: string;
  description?: string;
  loading?: boolean;
  tooltipText?: string;
  children: ReactNode;
  wrapInCard?: boolean;
  hasError?: boolean;
  showSectionSkeleton?: boolean;
  sectionSkeletonHeight?: number;
  sectionSurface?: GridCardSurface;
  titleSize?: number;
}

const FormSection = ({
  action,
  children,
  description,
  hasError = false,
  loading = false,
  sectionSkeletonHeight = 400,
  sectionSurface = "inset",
  showSectionSkeleton = false,
  title,
  titleSize,
  tooltipText,
  wrapInCard = true
}: FormSectionProps) => {
  return (
    <Stack
      sx={{
        gap: LAYOUT_GAP,
        width: "100%"
      }}
    >
      {title || description ? (
        <FormSectionTitle
          action={action}
          title={title ?? ""}
          description={description}
          loading={loading}
          tooltipText={tooltipText}
          titleSize={titleSize}
        />
      ) : null}
      {showSectionSkeleton && loading ? (
        <Skeleton variant="rounded" height={sectionSkeletonHeight} />
      ) : (
        <>
          {wrapInCard ? (
            <GridCard
              surface={sectionSurface}
              container
              sx={(t) => ({
                borderColor: hasError
                  ? t.palette.error.main
                  : t.palette.border.subtle
              })}
            >
              {children}
            </GridCard>
          ) : (
            children
          )}
        </>
      )}
    </Stack>
  );
};

export {
  AuthFormContainer,
  FormSectionTitle,
  AuthFormCard,
  StyledFormCheckbox,
  FormComponentsErrorBox,
  AuthLogoLink,
  FormSection,
  FormSectionsContainer
};
